import {
  applyRemoteSram,
  applyRemoteState,
  AUTOSAVE_SLOT,
  equalBytes,
  getSaveRecord,
  listAllSaves,
  listAllStates,
  loadState,
  markSaveConflicted,
  markSaveSynced,
  markStateConflicted,
  markStatePendingWithEtag,
  markStateSynced,
  SHARED_PSX_SAVE_KEY,
  SHARED_PSX_SAVE_SHA1,
  isPsxMemcardSaveKey,
  psxMemcardSha1FromSaveKey,
  allPsxMemcardBindings,
} from './saveRepository';
import {
  getRomMeta,
  listRoms,
  findRomBySha1,
  deleteRom,
  toCloudRomMeta,
  upsertCloudRomMeta,
  type CloudRomMeta,
} from './romRepository';
import { preferredBackendIdFor } from '../emulator/registry';
import {
  clearConflict,
  conflictId,
  getConflict,
  listConflicts,
  recordConflict,
} from './conflictRepository';
import {
  listTombstones,
  mergeTombstones,
  pruneRevivedTombstones,
  saveTombstones,
  type LibraryTombstone,
} from './libraryTombstones';
import {
  canonicalEtag,
  keyOf,
  parseBlobPath,
  planSync,
  toBlobPath,
  type LocalItem,
  type RemoteItem,
  type SyncAction,
} from './syncPlanner';

/**
 * オンプレ Blob（Azurite）との双方向セーブデータ同期。
 *
 * 設計方針:
 *  - ブラウザにストレージキーを持たせない。/api/sas が短命の SAS URL を発行し、
 *    クライアントはその URL に対して PUT / GET するだけにする。
 *  - Blob 名は ROM の SHA-1 にする。同じゲームなら端末が違っても同じ blob を指す。
 *  - 競合は ETag (If-Match / If-None-Match) で検出し、**どちらも消さずに保留**する。
 *    解決はユーザーが選ぶ（conflictRepository + SyncConflictDialog）。
 *  - オートセーブ（スロット -1）は端末ローカル専用。サーバー同期しない。
 *  - プレイ終了・バックグラウンド移行時に requestSync で早めにサーバーへ載せる。
 *  - ROM 本体は送らない。容量的にも権利的にも割に合わない。
 *  - ライブラリ一覧は library.json（メタのみ）を同期し、未所持 ROM は再投入で紐づける。
 *
 * サーバ側の実装は api/src/functions/{sas,list}.mjs を参照。
 */

const LIBRARY_PATH = 'library.json';

interface CloudLibraryDoc {
  version: 1;
  updatedAt: number;
  roms: CloudRomMeta[];
  /** 削除済み SHA-1。同期で復活させないための墓標。 */
  deleted?: Array<{ sha1: string; deletedAt: number }>;
}

export interface SasResponse {
  url: string;
  expiresAt: string;
}

export interface RemoteListEntry {
  path: string;
  etag: string;
  size: number;
  updatedAt: number;
  backendId?: string;
}

export interface SyncResult {
  uploaded: number;
  downloaded: number;
  inSync: number;
  /** 対応する ROM を持っていないので取り込めなかった数 */
  skipped: number;
  /** 新たに検出した競合（既知の未解決は含まない） */
  conflicts: number;
  /** 中身一致などで自動解消した競合 */
  conflictsAutoResolved: number;
  /** サーバーからライブラリメタを取り込んだ件数 */
  libraryImported: number;
  /**
   * 実際に中身が変わった取り込みのラベル（トースト用）。
   * ETag だけ直した再取得は含めない。
   */
  downloadedLabels: string[];
  errors: Array<{ path: string; message: string }>;
}

const SAS_ENDPOINT = '/api/sas';
const LIST_ENDPOINT = '/api/list';
const CLOUD_ENABLED = import.meta.env.VITE_CLOUD_SYNC_ENABLED === 'true';

/** SAS はレスポンス毎に有効期限があるので、期限内はメモリキャッシュする。 */
const sasCache = new Map<string, { url: string; expires: number }>();

export class CloudAuthError extends Error {
  constructor() {
    super('サーバー同期にはサインインが必要です');
    this.name = 'CloudAuthError';
  }
}

export class CloudUnavailableError extends Error {
  constructor(message = 'サーバー同期が構成されていません') {
    super(message);
    this.name = 'CloudUnavailableError';
  }
}

export function isCloudEnabled(): boolean {
  return CLOUD_ENABLED;
}

/* -------------------------------------------------------------------------- */
/* 認証                                                                        */
/* -------------------------------------------------------------------------- */

export interface ClientPrincipal {
  userId: string;
  userDetails: string;
  identityProvider: string;
  userRoles: string[];
  username?: string;
  role?: string;
  extraStateSlots?: number;
  extraPsxCards?: number;
  ownedGameIds?: string[];
  fullAccess?: boolean;
}

/** Passkey セッション優先。なければ SWA 組み込み認証へフォールバック。 */
export async function getCurrentUser(): Promise<ClientPrincipal | null> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'include' });
    if (res.ok) {
      const body = (await res.json()) as { clientPrincipal: ClientPrincipal | null };
      if (body.clientPrincipal) return body.clientPrincipal;
    }
  } catch {
    /* fall through */
  }

  try {
    const res = await fetch('/.auth/me', { credentials: 'include' });
    if (!res.ok) return null;
    const body = (await res.json()) as { clientPrincipal: ClientPrincipal | null };
    return body.clientPrincipal;
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* 低レベル: SAS / 一覧 / 転送                                                 */
/* -------------------------------------------------------------------------- */

async function getSas(blobPath: string, permission: 'r' | 'w' | 'd'): Promise<string> {
  const key = `${permission}:${blobPath}`;
  const cached = sasCache.get(key);
  // 期限の 60 秒前には取り直す
  if (cached && cached.expires - 60_000 > Date.now()) return cached.url;

  const res = await fetch(`${SAS_ENDPOINT}?path=${encodeURIComponent(blobPath)}&p=${permission}`, {
    credentials: 'include',
  });

  if (res.status === 401) throw new CloudAuthError();
  if (res.status === 503) throw new CloudUnavailableError();
  if (res.status === 402) {
    const denied = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(denied.error || 'サーバーの無料枠を超えています');
  }
  if (!res.ok) throw new Error(`SAS の取得に失敗しました (${res.status})`);

  const body = (await res.json()) as SasResponse;
  sasCache.set(key, { url: body.url, expires: Date.parse(body.expiresAt) });
  return body.url;
}

/** サーバ側に置いた一覧 API。自分のユーザー配下だけが返る。 */
async function listRemote(): Promise<RemoteItem[]> {
  const res = await fetch(LIST_ENDPOINT, { credentials: 'include' });
  if (res.status === 401) throw new CloudAuthError();
  if (res.status === 503) throw new CloudUnavailableError();
  if (!res.ok) throw new Error(`一覧の取得に失敗しました (${res.status})`);

  const body = (await res.json()) as { items: RemoteListEntry[] };
  const items: RemoteItem[] = [];

  for (const entry of body.items) {
    const key = parseBlobPath(entry.path);
    if (!key) continue; // 想定外のパスは無視する
    // オートセーブは端末ローカル専用。サーバーにあっても取り込まない。
    if (key.kind === 'state' && key.slot === AUTOSAVE_SLOT) continue;
    items.push({
      ...key,
      etag: canonicalEtag(entry.etag) ?? entry.etag,
      updatedAt: entry.updatedAt,
      size: entry.size,
      ...(entry.backendId ? { backendId: entry.backendId } : {}),
    });
  }
  return items;
}

async function uploadBlob(
  path: string,
  data: ArrayBuffer,
  updatedAt: number,
  expectedEtag: string | undefined,
  meta: Record<string, string> = {},
): Promise<string> {
  const url = await getSas(path, 'w');

  const headers: Record<string, string> = {
    'x-ms-blob-type': 'BlockBlob',
    'Content-Type': 'application/octet-stream',
    // 同期メタをブロブに埋めておくと、別端末が中身を読まずに新旧を判定できる
    'x-ms-meta-updatedat': String(updatedAt),
    ...Object.fromEntries(Object.entries(meta).map(([k, v]) => [`x-ms-meta-${k}`, v])),
  };

  // 既知の ETag があるなら「その版から変わっていない場合だけ書く」
  const match = canonicalEtag(expectedEtag);
  if (match) headers['If-Match'] = match;

  if (data.byteLength === 0) {
    throw new Error('アップロードするデータが空です');
  }

  // 共有 ArrayBuffer の事故を避けるためコピーして送る
  const body = data.slice(0);
  const res = await fetch(url, { method: 'PUT', headers, body });

  if (res.status === 412) throw new PreconditionFailed(path);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const fromXml = text.match(/<Message>([^<]+)/)?.[1]?.trim();
    const detail = (fromXml || text).replace(/\s+/g, ' ').slice(0, 240);
    throw new Error(
      detail
        ? `アップロードに失敗しました (${res.status}): ${detail}`
        : `アップロードに失敗しました (${res.status})`,
    );
  }

  return canonicalEtag(res.headers.get('ETag')) ?? '';
}

class PreconditionFailed extends Error {
  constructor(public path: string) {
    super('サーバー側が先に更新されています');
    this.name = 'PreconditionFailed';
  }
}

async function downloadBlob(
  path: string,
): Promise<{ data: ArrayBuffer; etag: string; updatedAt: number; backendId?: string } | null> {
  const url = await getSas(path, 'r');
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`ダウンロードに失敗しました (${res.status})`);

  const backendId = res.headers.get('x-ms-meta-backendid');
  const data = await res.arrayBuffer();
  if (data.byteLength === 0) {
    throw new Error('ダウンロードしたデータが空です');
  }
  // HTML の誤配布（SPA fallback）をステートとして保存しない
  if (path !== LIBRARY_PATH && data.byteLength < 64) {
    const head = new TextDecoder().decode(data.slice(0, Math.min(32, data.byteLength))).trimStart();
    if (head.startsWith('<!') || head.startsWith('<html')) {
      throw new Error('サーバーがゲームデータではなく HTML を返しました');
    }
  }
  return {
    data,
    etag: canonicalEtag(res.headers.get('ETag')) ?? '',
    updatedAt: Number(res.headers.get('x-ms-meta-updatedat') ?? 0),
    ...(backendId ? { backendId } : {}),
  };
}

/* -------------------------------------------------------------------------- */
/* ローカル側の一覧作成                                                        */
/* -------------------------------------------------------------------------- */

async function collectLocal(): Promise<{ items: LocalItem[]; sha1ById: Map<string, string> }> {
  const roms = await listRoms();
  const sha1ById = new Map(roms.map((r) => [r.id, r.sha1]));
  // PS1 は共有カードだけを同期する。ROM 単位のレガシー SRAM が残っていると
  // 使っていないレコードが毎回競合を起こす。
  const legacyPsxRomIds = new Set(roms.filter((r) => r.system === 'psx').map((r) => r.id));

  const items: LocalItem[] = [];

  for (const save of await listAllSaves()) {
    if (legacyPsxRomIds.has(save.romId)) continue;

    // PS1 メモリーカードは ROM に属さないので予約 SHA-1 で同期する
    let sha1: string | undefined;
    if (isPsxMemcardSaveKey(save.romId)) {
      sha1 = psxMemcardSha1FromSaveKey(save.romId);
    } else {
      sha1 = sha1ById.get(save.romId);
    }
    if (!sha1) continue; // ROM が消えているセーブ / 未知のカードは同期対象外
    items.push({
      sha1,
      kind: 'sram',
      romId: save.romId,
      updatedAt: save.updatedAt,
      etag: canonicalEtag(save.etag),
      // conflict は dirty に含めない（毎同期で再検出されないようにする）
      dirty: save.syncState === 'pending' || save.syncState === 'local',
    });
  }

  for (const state of await listAllStates()) {
    // オートセーブは端末固有の保険。サーバーへ出すと競合の主因になるので除外する。
    if (state.slot === AUTOSAVE_SLOT) continue;
    const sha1 = sha1ById.get(state.romId);
    if (!sha1) continue;
    items.push({
      sha1,
      kind: 'state',
      slot: state.slot,
      romId: state.romId,
      updatedAt: state.createdAt,
      etag: canonicalEtag(state.etag),
      dirty: state.syncState === 'pending' || state.syncState === 'local',
    });
  }

  return { items, sha1ById };
}

/* -------------------------------------------------------------------------- */
/* 同期本体                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * ローカルとサーバーを突き合わせて双方向に同期する。
 *
 * 競合したものは**どちらも消さず**に conflicts ストアへ積み、
 * ユーザーが選ぶまで保留する。
 */
export async function sync(): Promise<SyncResult> {
  const result: SyncResult = {
    uploaded: 0,
    downloaded: 0,
    inSync: 0,
    skipped: 0,
    conflicts: 0,
    conflictsAutoResolved: 0,
    libraryImported: 0,
    downloadedLabels: [],
    errors: [],
  };
  if (!CLOUD_ENABLED) return result;

  // 先にライブラリメタを揃え、セーブを取り込める romId を確保する
  try {
    result.libraryImported = await syncLibrary();
  } catch (e) {
    if (e instanceof CloudAuthError || e instanceof CloudUnavailableError) throw e;
    result.errors.push({
      path: LIBRARY_PATH,
      message: e instanceof Error ? e.message : String(e),
    });
  }

  const [{ items: local, sha1ById }, remote, roms, heldConflicts] = await Promise.all([
    collectLocal(),
    listRemote(),
    listRoms(),
    listConflicts(),
  ]);

  const romIdBySha1 = new Map(roms.map((r) => [r.sha1, r.id]));
  // メモリーカードは ROM を持たないが、取り込み先は常に存在する。
  for (const b of allPsxMemcardBindings()) {
    romIdBySha1.set(b.sha1, b.saveKey);
  }

  const holdKeys = new Set<string>();
  for (const c of heldConflicts) {
    const sha1 = isPsxMemcardSaveKey(c.romId)
      ? psxMemcardSha1FromSaveKey(c.romId)
      : sha1ById.get(c.romId);
    if (!sha1) continue;
    holdKeys.add(
      keyOf({
        sha1,
        kind: c.kind,
        ...(c.kind === 'state' ? { slot: c.slot } : {}),
      }),
    );
  }

  const actions = planSync(local, remote, romIdBySha1, { holdKeys });

  for (const action of actions) {
    try {
      await applyAction(action, result);
    } catch (e) {
      if (e instanceof CloudAuthError || e instanceof CloudUnavailableError) throw e;
      result.errors.push({
        path: actionPath(action),
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }

  return result;
}

function actionPath(action: SyncAction): string {
  switch (action.type) {
    case 'upload':
      return toBlobPath(action.local);
    case 'download':
    case 'skip':
      return toBlobPath(action.remote);
    case 'conflict':
      return toBlobPath(action.local);
    case 'in-sync':
      return toBlobPath(action.key);
  }
}

async function applyAction(action: SyncAction, result: SyncResult): Promise<void> {
  switch (action.type) {
    case 'in-sync':
      result.inSync++;
      return;

    case 'skip':
      result.skipped++;
      return;

    case 'upload': {
      try {
        await uploadLocal(action.local, action.expectedEtag);
        result.uploaded++;
      } catch (e) {
        if (!(e instanceof PreconditionFailed)) throw e;
        // 一覧を取ってからアップロードするまでの間に別端末が書いた。
        // 上書きせず競合として保留する。
        const fresh = await downloadBlob(toBlobPath(action.local));
        if (fresh) {
          const stashed = await stashConflict(
            action.local,
            {
              ...action.local,
              etag: fresh.etag,
              updatedAt: fresh.updatedAt,
              size: fresh.data.byteLength,
            },
            fresh.data,
          );
          if (stashed === 'new') result.conflicts++;
          else if (stashed === 'same') {
            result.inSync++;
            result.conflictsAutoResolved++;
          }
        }
      }
      return;
    }

    case 'download': {
      const outcome = await downloadRemote(action.remote, action.localRomId);
      if (outcome === 'applied') {
        result.downloaded++;
        result.downloadedLabels.push(await labelForDownload(action.localRomId, action.remote));
      } else if (outcome === 'etag-only') {
        // 中身は同じで ETag だけ揃えた。通知すると「何度も取り込んだ」ように見える。
        result.inSync++;
      }
      return;
    }

    case 'conflict': {
      const fresh = await downloadBlob(toBlobPath(action.remote));
      if (!fresh) {
        // 一覧取得後にリモートが消えた。ローカルを正として送る。
        await uploadLocal(action.local, undefined);
        result.uploaded++;
        return;
      }
      const stashed = await stashConflict(action.local, action.remote, fresh.data);
      if (stashed === 'new') result.conflicts++;
      else if (stashed === 'same') {
        result.inSync++;
        result.conflictsAutoResolved++;
      }
      return;
    }
  }
}

/**
 * アップロード後の ETag を確定する。
 * CORS で応答ヘッダが読めない場合は一覧 API（サーバ側で etag を返す）から拾う。
 * アップロード前のカタログは古い版のままなので使わない。
 */
async function resolveUploadedEtag(local: LocalItem, headerEtag: string): Promise<string> {
  const fromHeader = canonicalEtag(headerEtag);
  if (fromHeader) return fromHeader;

  try {
    const listed = await listRemote();
    const found = listed.find((r) => keyOf(r) === keyOf(local));
    const fromList = canonicalEtag(found?.etag);
    if (fromList) return fromList;
  } catch {
    /* list 失敗時はダウンロードで拾う */
  }

  // CORS で ETag が読めず list も空なら、自分が書いた blob を GET して確定する。
  // これを怠ると pending のまま古い etag が残り、終了直後の同期で必ず競合になる。
  try {
    const fresh = await downloadBlob(toBlobPath(local));
    return canonicalEtag(fresh?.etag) ?? '';
  } catch {
    return '';
  }
}

async function uploadLocal(
  local: LocalItem,
  expectedEtag: string | undefined,
): Promise<boolean> {
  const rom =
    isPsxMemcardSaveKey(local.romId)
      ? {
          title: memcardTitle(local.romId),
          sha1: psxMemcardSha1FromSaveKey(local.romId) ?? SHARED_PSX_SAVE_SHA1,
        }
      : await getRomMeta(local.romId);
  if (!rom) return false;

  const path = toBlobPath(local);

  if (local.kind === 'sram') {
    const record = await getSaveRecord(local.romId);
    if (!record) return false;

    const generation = record.updatedAt;
    let etag = await uploadBlob(path, record.data, record.updatedAt, expectedEtag, {
      romtitle: encodeURIComponent(rom.title),
    });
    etag = await resolveUploadedEtag(local, etag);
    if (!etag) return true; // 空 ETag で synced にすると次回偽競合になる

    const current = await getSaveRecord(local.romId);
    if (!current) return false;

    // 世代付き更新。flush と重なっても最新データを synced と誤認しない。
    await markSaveSynced(local.romId, etag, generation);
    return true;
  }

  const record = await loadState(local.romId, local.slot ?? 0);
  if (!record) return false;

  const generation = record.createdAt;
  let etag = await uploadBlob(path, record.data, record.createdAt, expectedEtag, {
    romtitle: encodeURIComponent(rom.title),
    // 値は ASCII のみ（Azurite メタ制約）。コロン等はそのまま可。
    backendid: String(record.backendId || 'unknown').slice(0, 128),
  });
  etag = await resolveUploadedEtag(local, etag);
  if (!etag) return true;

  const current = await loadState(local.romId, local.slot ?? 0);
  if (!current) return false;

  if (current.createdAt !== generation) {
    await markStatePendingWithEtag(local.romId, record.slot, etag);
    return true;
  }

  await markStateSynced(local.romId, record.slot, etag);
  return true;
}

/**
 * リモート取り込み。
 *
 * - applied: 中身が変わった（トースト対象）
 * - etag-only: バイト列は同じで、一覧 API の ETag だけ揃えた
 *   （GET の ETag が CORS で欠落／表記ゆれすると、毎回 download 判定になる）
 * - false: 取得失敗
 *
 * 次回の planSync は list の ETag と比較するので、保存する ETag は list 側を優先する。
 */
async function downloadRemote(
  remote: RemoteItem,
  romId: string,
): Promise<'applied' | 'etag-only' | false> {
  const blob = await downloadBlob(toBlobPath(remote));
  if (!blob) return false;

  const data = new Uint8Array(blob.data);
  const updatedAt = blob.updatedAt || remote.updatedAt;
  // list の ETag を正とする。GET ヘッダは CORS で欠ける／弱い ETag になることがある。
  const etag = canonicalEtag(remote.etag) || canonicalEtag(blob.etag) || '';

  if (remote.kind === 'sram') {
    const existing = await getSaveRecord(romId);
    if (existing && equalBytes(new Uint8Array(existing.data), data)) {
      if (etag && (canonicalEtag(existing.etag) !== etag || existing.syncState !== 'synced')) {
        await markSaveSynced(romId, etag);
      }
      return 'etag-only';
    }
    await applyRemoteSram(romId, data, etag, updatedAt);
    return 'applied';
  }

  const slot = remote.slot ?? 0;
  const existing = await loadState(romId, slot);
  const rom = await getRomMeta(romId);
  const knownExisting =
    existing?.backendId && existing.backendId !== 'unknown' ? existing.backendId : undefined;
  const resolvedBackend =
    blob.backendId ||
    remote.backendId ||
    knownExisting ||
    (rom ? preferredBackendIdFor(rom.system) : undefined) ||
    'unknown';

  if (existing && equalBytes(new Uint8Array(existing.data), data)) {
    const needsBackendFix = !knownExisting;
    if (
      needsBackendFix ||
      (etag && (canonicalEtag(existing.etag) !== etag || existing.syncState !== 'synced'))
    ) {
      // 中身同じでも backendId 欠落を直す（読込不能の主因）
      await applyRemoteState(
        romId,
        slot,
        data,
        etag || existing.etag || '',
        existing.createdAt,
        resolvedBackend,
      );
    }
    return 'etag-only';
  }
  await applyRemoteState(romId, slot, data, etag, updatedAt, resolvedBackend);
  return 'applied';
}

async function labelForDownload(romId: string, remote: RemoteItem): Promise<string> {
  if (isPsxMemcardSaveKey(romId)) return memcardTitle(romId);
  const rom = await getRomMeta(romId);
  const title = rom?.title ?? romId.slice(0, 8);
  if (remote.kind === 'sram') return `${title}（SRAM）`;
  return `${title}（ステート ${remote.slot ?? 0}）`;
}

type StashResult = 'new' | 'same' | 'updated';

/**
 * 競合を保留する。ローカルとリモートの中身が同じなら競合にせず同期済みにする。
 */
async function stashConflict(
  local: LocalItem,
  remote: RemoteItem,
  remoteData: ArrayBuffer,
): Promise<StashResult> {
  const rom = isPsxMemcardSaveKey(local.romId)
    ? { title: memcardTitle(local.romId) }
    : await getRomMeta(local.romId);
  const slot = local.slot ?? 0;
  const id = conflictId(local.romId, local.kind, slot);
  const remoteBytes = new Uint8Array(remoteData);

  let localBytes: Uint8Array | undefined;
  let localSize = 0;
  let localThumbnail: string | undefined;

  if (local.kind === 'sram') {
    const record = await getSaveRecord(local.romId);
    if (record) {
      localBytes = new Uint8Array(record.data);
      localSize = record.data.byteLength;
    }
  } else {
    const record = await loadState(local.romId, slot);
    if (record) {
      localBytes = new Uint8Array(record.data);
      localSize = record.data.byteLength;
      localThumbnail = record.thumbnail;
    }
  }

  // 中身が同じなら偽競合。ローカル本体は保ったままリモート etag で同期済みにする。
  // remote.etag が空でも同一バイトなら競合にしない（CORS / list 欠落対策）。
  const remoteEtag = canonicalEtag(remote.etag) ?? '';
  if (localBytes && equalBytes(localBytes, remoteBytes)) {
    if (remoteEtag) {
      if (local.kind === 'sram') {
        await markSaveSynced(local.romId, remoteEtag);
      } else {
        await markStateSynced(local.romId, slot, remoteEtag);
      }
    }
    await clearConflict(id);
    return 'same';
  }

  const existed = Boolean(await getConflict(id));

  if (local.kind === 'sram') {
    await markSaveConflicted(local.romId);
  } else {
    await markStateConflicted(local.romId, slot);
  }

  await recordConflict({
    id,
    romId: local.romId,
    romTitle: rom?.title ?? '(不明な ROM)',
    kind: local.kind,
    slot,
    localUpdatedAt: local.updatedAt,
    localSize,
    ...(localThumbnail ? { localThumbnail } : {}),
    remoteUpdatedAt: remote.updatedAt,
    remoteSize: remoteData.byteLength,
    remoteEtag: remote.etag,
    remoteData,
  });

  return existed ? 'updated' : 'new';
}

/* -------------------------------------------------------------------------- */
/* ライブラリメタ同期                                                          */
/* -------------------------------------------------------------------------- */

async function syncLibrary(): Promise<number> {
  let imported = 0;
  const localDeleted = await listTombstones();
  let remoteDeleted: LibraryTombstone[] = [];
  let remoteRoms: CloudRomMeta[] = [];

  const remoteBlob = await downloadBlob(LIBRARY_PATH);
  if (remoteBlob) {
    try {
      const doc = JSON.parse(new TextDecoder().decode(remoteBlob.data)) as CloudLibraryDoc;
      if (Array.isArray(doc.roms)) remoteRoms = doc.roms;
      if (Array.isArray(doc.deleted)) remoteDeleted = doc.deleted;
    } catch (e) {
      console.warn('[sync] invalid library.json', e);
    }
  }

  const livingBefore = await listRoms();
  // 手元にある ROM は「再投入」扱いで墓標より優先（addedAt が削除時刻以降なら外す）
  let deleted = pruneRevivedTombstones(
    mergeTombstones(localDeleted, remoteDeleted),
    livingBefore.map((r) => ({ sha1: r.sha1, addedAt: r.addedAt })),
  );
  const deletedSha1 = new Set(deleted.map((d) => d.sha1));
  await saveTombstones(deleted);

  for (const sha1 of deletedSha1) {
    const rom = await findRomBySha1(sha1);
    if (rom) await deleteRom(rom.id);
  }

  for (const entry of remoteRoms) {
    if (!entry?.sha1 || !entry.system || !entry.title) continue;
    if (deletedSha1.has(entry.sha1)) continue;
    const before = await findRomBySha1(entry.sha1);
    await upsertCloudRomMeta(entry);
    if (!before) imported++;
  }

  const local = await listRoms();
  deleted = pruneRevivedTombstones(
    deleted,
    local.map((r) => ({ sha1: r.sha1, addedAt: r.addedAt })),
  );
  await saveTombstones(deleted);

  const doc: CloudLibraryDoc = {
    version: 1,
    updatedAt: Date.now(),
    roms: local.map(toCloudRomMeta),
    deleted,
  };
  const bytes = new TextEncoder().encode(JSON.stringify(doc));
  const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  await uploadBlob(LIBRARY_PATH, ab, doc.updatedAt, undefined);
  return imported;
}

/* -------------------------------------------------------------------------- */
/* 競合の解決                                                                  */
/* -------------------------------------------------------------------------- */

export type Resolution =
  /** ローカルを正としてサーバーを上書きする */
  | 'local'
  /** サーバーを正としてローカルを置き換える */
  | 'remote'
  /** リモート側を空きスロットへ退避し、両方残す（ステートのみ） */
  | 'keep-both';

export async function resolveConflict(id: string, resolution: Resolution): Promise<void> {
  const conflict = await getConflict(id);
  if (!conflict) return;

  const isSharedPsx = isPsxMemcardSaveKey(conflict.romId);
  const rom = isSharedPsx ? null : await getRomMeta(conflict.romId);
  if (!isSharedPsx && !rom) {
    // ROM ごと消えているなら競合を保持する意味がない
    await clearConflict(id);
    return;
  }
  const sha1 = isSharedPsx
    ? (psxMemcardSha1FromSaveKey(conflict.romId) ?? SHARED_PSX_SAVE_SHA1)
    : rom!.sha1;

  switch (resolution) {
    case 'local': {
      // リモートの現在の版を踏まえて上書きする
      const ok = await uploadLocal(
        {
          sha1,
          kind: conflict.kind,
          ...(conflict.kind === 'state' ? { slot: conflict.slot } : {}),
          romId: conflict.romId,
          updatedAt: conflict.localUpdatedAt,
          etag: conflict.remoteEtag,
          dirty: true,
        },
        conflict.remoteEtag,
      );
      if (!ok) throw new Error('ローカルのセーブをサーバーへ送れませんでした');
      break;
    }

    case 'remote': {
      const data = new Uint8Array(conflict.remoteData);
      if (conflict.kind === 'sram') {
        await applyRemoteSram(conflict.romId, data, conflict.remoteEtag, conflict.remoteUpdatedAt);
      } else {
        await applyRemoteState(
          conflict.romId,
          conflict.slot,
          data,
          conflict.remoteEtag,
          conflict.remoteUpdatedAt,
        );
      }
      break;
    }

    case 'keep-both': {
      if (conflict.kind !== 'state') {
        // SRAM はスロットの概念が無いので両立できない
        throw new Error('セーブデータ (SRAM) は「両方残す」を選べません');
      }
      const slot = await findFreeSlot(conflict.romId);
      if (slot === null) throw new Error('空きスロットがありません。不要なステートを削除してください。');

      await applyRemoteState(
        conflict.romId,
        slot,
        new Uint8Array(conflict.remoteData),
        '', // 別スロットなので ETag は引き継がない（次回 upload される）
        conflict.remoteUpdatedAt,
      );
      // ローカル側は元のスロットのまま、サーバーへ送り直す
      const ok = await uploadLocal(
        {
          sha1,
          kind: 'state',
          slot: conflict.slot,
          romId: conflict.romId,
          updatedAt: conflict.localUpdatedAt,
          etag: conflict.remoteEtag,
          dirty: true,
        },
        conflict.remoteEtag,
      );
      if (!ok) throw new Error('ローカルのセーブステートをサーバーへ送れませんでした');
      break;
    }
  }

  await clearConflict(id);
}

async function findFreeSlot(romId: string): Promise<number | null> {
  const states = await listAllStates();
  const used = new Set(states.filter((s) => s.romId === romId).map((s) => s.slot));
  for (let i = 0; i < 10; i++) if (!used.has(i)) return i;
  return null;
}

function memcardTitle(saveKey: string): string {
  const id = saveKey === SHARED_PSX_SAVE_KEY ? '0' : saveKey.replace(/^shared:psx:/, '');
  const n = Number(id);
  return Number.isFinite(n) ? `PS1 メモリーカード ${n + 1}` : 'PS1 メモリーカード';
}

/* -------------------------------------------------------------------------- */
/* 自動同期 / 即時同期                                                         */
/* -------------------------------------------------------------------------- */

const SYNC_DEBOUNCE_MS = 800;

type SyncRunner = () => void;

let syncRunner: SyncRunner | null = null;
let syncDebounceTimer: number | undefined;

/**
 * セーブ直後などにサーバー同期を催促する。
 * 短時間に連続呼び出しされても 1 回にまとめる（pagehide + visibility の二重発火対策）。
 * startAutoSync が動いていない（未ログイン等）場合は何もしない。
 */
export function requestSync(): void {
  if (!CLOUD_ENABLED || !syncRunner) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;

  if (syncDebounceTimer !== undefined) window.clearTimeout(syncDebounceTimer);
  syncDebounceTimer = window.setTimeout(() => {
    syncDebounceTimer = undefined;
    syncRunner?.();
  }, SYNC_DEBOUNCE_MS);
}

/**
 * オンライン復帰時と一定間隔で同期を試みる。
 * 失敗しても静かに諦める（ゲームプレイを妨げないことが最優先）。
 */
export function startAutoSync(
  intervalMs = 5 * 60_000,
  onResult?: (r: SyncResult) => void,
): () => void {
  if (!CLOUD_ENABLED) return () => undefined;

  let running = false;
  let queued = false;

  const run = () => {
    if (!navigator.onLine) return;
    if (running) {
      // セーブ直後の催促が走行中の同期に重なったら、終わってからもう一度
      queued = true;
      return;
    }
    running = true;
    void sync()
      .then((r) => onResult?.(r))
      .catch((e) => {
        // 未サインインは通常状態なので騒がない
        if (!(e instanceof CloudAuthError)) console.warn('[sync] failed', e);
      })
      .finally(() => {
        running = false;
        if (queued) {
          queued = false;
          run();
        }
      });
  };

  syncRunner = run;

  const onOnline = () => run();
  window.addEventListener('online', onOnline);

  const timer = window.setInterval(run, intervalMs);
  run();

  return () => {
    if (syncRunner === run) syncRunner = null;
    if (syncDebounceTimer !== undefined) {
      window.clearTimeout(syncDebounceTimer);
      syncDebounceTimer = undefined;
    }
    window.removeEventListener('online', onOnline);
    clearInterval(timer);
  };
}

/**
 * 指定 ROM（SHA-1）のサーバー上セーブを消す。
 * library の墓標とは別。セーブだけ消したいときに使う。
 */
export async function deleteCloudSavesForSha1(sha1: string): Promise<number> {
  if (!CLOUD_ENABLED) return 0;
  if (!/^[0-9a-f]{40}$/.test(sha1)) return 0;

  let removed = 0;
  const remote = await listRemote();
  for (const item of remote) {
    if (item.sha1 !== sha1) continue;
    const path = toBlobPath(item);
    try {
      const url = await getSas(path, 'd');
      const res = await fetch(url, { method: 'DELETE' });
      // 404 は既に無いので成功扱い
      if (res.ok || res.status === 404) removed++;
    } catch (e) {
      if (e instanceof CloudAuthError || e instanceof CloudUnavailableError) throw e;
      console.warn('[sync] cloud save delete failed', path, e);
    }
  }
  return removed;
}
